import useEventCallback from '@hooks/use-event-callback';
import useLocalStorage from '@hooks/use-local-storage';
import { useEffect, useRef, useState } from 'react';

export function useCooldown(toggleKey: string, cooldownMs: number): [boolean, () => void] {
  const [lastToggleDateString, setLastToggleDateString] = useLocalStorage<string | undefined>(toggleKey, undefined);
  const [isOnCooldown, setIsOnCooldown] = useState(false);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toggle = useEventCallback(() => {
    setLastToggleDateString(new Date().toISOString());
    setIsOnCooldown(true);
  });

  useEffect(() => {
    const lastToggled = lastToggleDateString ? new Date(lastToggleDateString) : undefined;
    if (lastToggled === undefined) {
      setIsOnCooldown(false);
      return;
    }

    const timeoutMs = lastToggled.getTime() + cooldownMs - new Date().getTime();

    if (timeoutMs <= 0) {
      setIsOnCooldown(false);
      return;
    }

    setIsOnCooldown(true);
    // Clear any pending timer before scheduling a new one
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    // Set new timeout
    timeoutIdRef.current = setTimeout(() => {
      setIsOnCooldown(false);
      timeoutIdRef.current = null;
    }, timeoutMs);
  }, [cooldownMs, lastToggleDateString]);

  useEffect(() => {
    // Clear the timeout when the component unmounts to prevent memory leaks
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, []);

  return [isOnCooldown, toggle];
}
